import React, { useEffect, useReducer, useState } from 'react';
import { Button, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { CardControls, CardSection } from 'shared/components/cardControls';
import {
  createTenantSatellite,
  deleteTenantSatellite,
  getTenantSatellite,
  updateTenantSatellite,
} from 'shared/api/tenants';
import StickyHeadTable from 'shared/components/table/StickyHeadTable';
import { useRequest } from 'shared/hooks';
import { BUTTON_COLORS, VARIANTS } from 'shared/constants';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { Section } from 'shared/components/styled/layouts';
import { TextEditor } from 'shared/components/data-display/editors';

const receiverNumbers = [1, 2, 3, 4, 5, 6];
const columns = [
  {
    name: 'Receiver ID',
    jsonKey: 'id',
  },
  {
    name: 'Model#',
    jsonKey: 'model',
  },
  {
    name: 'Serial#',
    jsonKey: 'serial',
  },
  {
    name: 'Card#',
    jsonKey: 'card',
  },
];

function getKey(receiverNum, jsonKey) {
  return `receiver_${receiverNum}_${jsonKey}`;
}

// Satellite data is an object with numbered attributes (receiver_1, receiver_2, receiver_3, etc.).
// Converts object into rows.
function parseReceivers(raw) {
  const result = [];

  receiverNumbers.forEach((receiverNum) => {
    const row = { num: receiverNum };
    let rowHasValue = false;

    columns.forEach(({ jsonKey }) => {
      const val = raw[getKey(receiverNum, jsonKey)];
      row[jsonKey] = val;
      if (!rowHasValue) {
        rowHasValue = !!val;
      }
    });
    if (rowHasValue) {
      result.push(row);
    }
  });

  return result;
}

/**
 * @param {Object} props
 * @param {Object} props.data - Receiver data object
 * @param {Function} props.onChange - Function to handle change events
 * @param {Function} props.onDelete - Function to handle delete action
 * @param {Object} props.updatedData - Updated receiver data object
 */
function ReceiverForm(props) {
  const {
    updatedData,
    data,
    onChange,
    onDelete,
  } = props;

  const getValue = (key) => (
    (updatedData[key] === undefined ? data[key] : updatedData[key]) || ''
  );

  return (
    <>
      <TextEditor
        name="account_number"
        label="Account Number"
        value={getValue('account_number')}
        setValue={onChange('account_number')}
      />
      {receiverNumbers.map((receiverNum) => (
        <Grid container columnSpacing={2} rowSpacing={1} key={receiverNum}>
          {columns.map(({ name, jsonKey }) => (
            <Grid size={{ lg: 3, xs: 12 }} key={getKey(receiverNum, jsonKey)}>
              <TextEditor
                name={getKey(receiverNum, jsonKey)}
                label={name}
                setValue={onChange(getKey(receiverNum, jsonKey))}
                value={getValue(getKey(receiverNum, jsonKey))}
              />
            </Grid>
          ))}
        </Grid>
      ))}
      {data.tenant_id && (
        <Grid
          container
          sx={{
            justifyContent: 'flex-end',
            mt: 2,
          }}
        >
          <Button
            variant={VARIANTS.contained}
            color={BUTTON_COLORS.error}
            onClick={onDelete}
          >
            Remove All
          </Button>
        </Grid>
      )}
    </>
  );
}

/**
 * @param {Object} props
 * @param {Object} props.tenantState - Tenant state object
 */
function TenantReceivers(props) {
  const {
    tenantState,
  } = props;

  const tenantId = tenantState.data.id;
  const { request, isLoading } = useRequest();
  const [receiverRows, setReceiverRows] = useState([]);
  const [state, dispatch] = useReducer(dataReducer, initialDataReducerState);

  const {
    data,
    updatedData,
    dirty,
  } = state;

  useEffect(() => {
    (async () => {
      const result = await request(getTenantSatellite(tenantId));
      if (result && result.data) {
        dispatch({ action: DataReducerActions.set, payload: result.data });
        setReceiverRows(parseReceivers(result.data));
      } else if (result && result.data === null) {
        dispatch({ action: DataReducerActions.set, payload: {} });
      }
    })();
  }, [request, tenantId]);

  const onChange = (key) => (value) => {
    dispatch({ action: DataReducerActions.update, payload: { key, value } });
  };

  const onDelete = async () => {
    const result = await request(deleteTenantSatellite(tenantId));
    if (result) {
      setReceiverRows([]);
      dispatch({ action: DataReducerActions.set, payload: {} });
    }
  };

  const saveChanges = async () => {
    let result;

    if (!data || !data.tenant_id) {
      result = await request(createTenantSatellite(tenantId, updatedData));
    } else {
      result = await request(updateTenantSatellite(tenantId, updatedData));
    }

    if (result) {
      dispatch({ action: DataReducerActions.set, payload: result });
      setReceiverRows(parseReceivers(result));
    }
  };

  if (state.data === undefined) {
    return <CardSection><CardControls editEnabled={false} /></CardSection>;
  }

  return (
    <CardSection editEnabled={tenantState.editEnabled} saveChanges={saveChanges}>
      <CardControls
        clearChanges={() => dispatch({ action: DataReducerActions.clear })}
        dirty={dirty}
        disabled={isLoading}
        editEnabled={tenantState.editEnabled}
      />
      <Section>
        <Typography variant="h4" component="div">
          { state.data && state.data.account_number ? `Satellite Account: ${state.data.account_number}` : 'Satellite Account'}
        </Typography>
        {tenantState.editEnabled
          ? (
            <ReceiverForm
              data={data || {}}
              onChange={onChange}
              onDelete={onDelete}
              updatedData={updatedData}
            />
          ) : (
            <div>
              <StickyHeadTable
                rowId="num"
                columns={columns}
                noRowsText="No receivers saved"
                rows={receiverRows}
              />
            </div>
          )}
      </Section>
    </CardSection>
  );
}

export default TenantReceivers;
